import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { Bell, ArrowLeft, Check, RefreshCw, Filter } from 'lucide-react';

const TYPE_LABELS = {
  en: {
    all: 'All', new_bid: 'Bids', bid_accepted: 'Bids', bid_rejected: 'Bids',
    new_job: 'Jobs', job_completed: 'Jobs', new_message: 'Messages',
    new_review: 'Reviews', subscription_expiring: 'Subscription',
    subscription_expired: 'Subscription', renewal_retry: 'Subscription', subscription_payment: 'Subscription',
    verification_submitted: 'Verification', admin_verification_approved: 'Verification',
    admin_verification_changes_requested: 'Verification', admin_verification_rejected: 'Verification',
    verification_approved: 'Verification', verification_rejected: 'Verification', system: 'System'
  },
  de: {
    all: 'Alle', new_bid: 'Angebote', bid_accepted: 'Angebote', bid_rejected: 'Angebote',
    new_job: 'Aufträge', job_completed: 'Aufträge', new_message: 'Nachrichten',
    new_review: 'Bewertungen', subscription_expiring: 'Abo',
    subscription_expired: 'Abo', renewal_retry: 'Abo', subscription_payment: 'Abo',
    verification_submitted: 'Verifizierung', admin_verification_approved: 'Verifizierung',
    admin_verification_changes_requested: 'Verifizierung', admin_verification_rejected: 'Verifizierung',
    verification_approved: 'Verifizierung', verification_rejected: 'Verifizierung', system: 'System'
  },
  it: {
    all: 'Tutti', new_bid: 'Offerte', bid_accepted: 'Offerte', bid_rejected: 'Offerte',
    new_job: 'Lavori', job_completed: 'Lavori', new_message: 'Messaggi',
    new_review: 'Recensioni', subscription_expiring: 'Abbonamento',
    subscription_expired: 'Abbonamento', renewal_retry: 'Abbonamento', subscription_payment: 'Abbonamento',
    verification_submitted: 'Verifica', admin_verification_approved: 'Verifica',
    admin_verification_changes_requested: 'Verifica', admin_verification_rejected: 'Verifica',
    verification_approved: 'Verifica', verification_rejected: 'Verifica', system: 'Sistema'
  },
  fr: {
    all: 'Tous', new_bid: 'Offres', bid_accepted: 'Offres', bid_rejected: 'Offres',
    new_job: 'Emplois', job_completed: 'Emplois', new_message: 'Messages',
    new_review: 'Avis', subscription_expiring: 'Abonnement',
    subscription_expired: 'Abonnement', renewal_retry: 'Abonnement', subscription_payment: 'Abonnement',
    verification_submitted: 'Vérification', admin_verification_approved: 'Vérification',
    admin_verification_changes_requested: 'Vérification', admin_verification_rejected: 'Vérification',
    verification_approved: 'Vérification', verification_rejected: 'Vérification', system: 'Système'
  }
};

const TYPE_ICONS = {
  new_bid: '💼', bid_accepted: '✅', bid_rejected: '❌',
  new_job: '🔍', job_completed: '🏆', new_message: '💬',
  new_review: '⭐', subscription_expiring: '⏳', subscription_expired: '🔴',
  renewal_retry: '🔄', subscription_payment: '💳', verification_submitted: '📄',
  admin_verification_approved: '✅', admin_verification_changes_requested: '📝',
  admin_verification_rejected: '❌', verification_approved: '✅', verification_rejected: '❌',
  system: 'ℹ️'
};

const FILTER_CATEGORIES = ['all', 'new_bid', 'new_job', 'new_message', 'new_review', 'subscription_expiring', 'subscription_expired'];

const FILTER_DISPLAY = {
  en: { all: 'All', new_bid: 'Bids', new_job: 'Jobs', new_message: 'Messages', new_review: 'Reviews', subscription_expiring: 'Expiring', subscription_expired: 'Expired' },
  de: { all: 'Alle', new_bid: 'Angebote', new_job: 'Aufträge', new_message: 'Nachrichten', new_review: 'Bewertungen', subscription_expiring: 'Läuft ab', subscription_expired: 'Abgelaufen' },
  it: { all: 'Tutti', new_bid: 'Offerte', new_job: 'Lavori', new_message: 'Messaggi', new_review: 'Recensioni', subscription_expiring: 'In scadenza', subscription_expired: 'Scaduto' },
  fr: { all: 'Tous', new_bid: 'Offres', new_job: 'Emplois', new_message: 'Messages', new_review: 'Avis', subscription_expiring: 'Expirant', subscription_expired: 'Expiré' }
};

const PAGE_SIZE = 20;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { API, language, activeRole } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const labels = {
    en: { title: 'Notifications', back: 'Back', markAllRead: 'Mark all read', unreadOnly: 'Unread only', noNotifs: 'No notifications yet', loadMore: 'Load more', refresh: 'Refresh', showing: 'Showing' },
    de: { title: 'Benachrichtigungen', back: 'Zurück', markAllRead: 'Alle als gelesen markieren', unreadOnly: 'Nur ungelesene', noNotifs: 'Noch keine Benachrichtigungen', loadMore: 'Mehr laden', refresh: 'Aktualisieren', showing: 'Zeige' },
    it: { title: 'Notifiche', back: 'Indietro', markAllRead: 'Segna tutte come lette', unreadOnly: 'Solo non lette', noNotifs: 'Nessuna notifica ancora', loadMore: 'Carica altro', refresh: 'Aggiorna', showing: 'Mostrando' },
    fr: { title: 'Notifications', back: 'Retour', markAllRead: 'Tout marquer comme lu', unreadOnly: 'Non lus seulement', noNotifs: 'Pas encore de notifications', loadMore: 'Charger plus', refresh: 'Actualiser', showing: 'Affichage' }
  }[language] || { title: 'Notifications', back: 'Back', markAllRead: 'Mark all read', unreadOnly: 'Unread only', noNotifs: 'No notifications yet', loadMore: 'Load more', refresh: 'Refresh', showing: 'Showing' };

  const typeLabels = TYPE_LABELS[language] || TYPE_LABELS.en;
  const filterDisplay = FILTER_DISPLAY[language] || FILTER_DISPLAY.en;

  const fetchNotifications = useCallback(async (resetPage = false) => {
    try {
      setLoading(true);
      const currentPage = resetPage ? 0 : page;
      const skip = currentPage * PAGE_SIZE;
      let url = `${API}/notifications?limit=${PAGE_SIZE}&skip=${skip}`;
      if (activeRole) url += `&role=${activeRole}`;
      if (filter !== 'all') url += `&type=${filter}`;
      const response = await axios.get(url);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data.notifications || []);
      const tot = data.total || list.length;
      if (resetPage) {
        setNotifications(list);
        setPage(0);
      } else {
        setNotifications(prev => currentPage === 0 ? list : [...prev, ...list]);
      }
      setTotal(tot);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }, [API, activeRole, filter, page]);

  useEffect(() => {
    fetchNotifications(true);
  }, [filter, unreadOnly, activeRole]);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {});
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      const params = activeRole ? `?role=${activeRole}` : '';
      await axios.put(`${API}/notifications/mark-all-read${params}`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    try {
      const skip = nextPage * PAGE_SIZE;
      let url = `${API}/notifications?limit=${PAGE_SIZE}&skip=${skip}`;
      if (activeRole) url += `&role=${activeRole}`;
      if (filter !== 'all') url += `&type=${filter}`;
      const response = await axios.get(url);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data.notifications || []);
      setNotifications(prev => [...prev, ...list]);
    } catch {}
  };

  const getTimeAgo = (timestamp) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const lang = language || 'en';
    if (diffMins < 1) return lang === 'de' ? 'Gerade eben' : lang === 'it' ? 'Adesso' : lang === 'fr' ? 'À l\'instant' : 'Just now';
    if (diffMins < 60) return `${diffMins}${lang === 'de' ? ' Min' : lang === 'it' ? ' min fa' : lang === 'fr' ? ' min' : 'm ago'}`;
    if (diffHours < 24) return `${diffHours}${lang === 'de' ? ' Std' : lang === 'it' ? ' ore fa' : lang === 'fr' ? 'h' : 'h ago'}`;
    return `${diffDays}${lang === 'de' ? ' Tage' : lang === 'it' ? ' giorni fa' : lang === 'fr' ? 'j' : 'd ago'}`;
  };

  const displayedNotifications = unreadOnly
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notifications-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn" className="rounded-full" aria-label="Go back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{labels.title}</h1>
              {total > 0 && (
                <p className="text-xs text-gray-500">{labels.showing} {displayedNotifications.length} / {total}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fetchNotifications(true)}
              className="rounded-full"
              data-testid="refresh-btn"
              aria-label="Refresh notifications"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-red-600 hover:text-red-700 text-xs"
                data-testid="mark-all-read-page-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                {labels.markAllRead}
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Filters row */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {FILTER_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filter === cat
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
              }`}
              data-testid={`filter-${cat}`}
            >
              {filterDisplay[cat] || cat}
            </button>
          ))}
          <button
            onClick={() => setUnreadOnly(!unreadOnly)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              unreadOnly
                ? 'bg-red-100 text-red-700 border-red-300'
                : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
            }`}
            data-testid="unread-only-filter"
          >
            {labels.unreadOnly}
            {unreadCount > 0 && <span className="ml-1 bg-red-600 text-white rounded-full px-1.5 py-0.5 text-[9px]">{unreadCount}</span>}
          </button>
        </div>

        {/* Notification list */}
        {loading && notifications.length === 0 ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        ) : displayedNotifications.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="text-gray-400 text-sm">{labels.noNotifs}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {displayedNotifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm ${
                  !notif.read
                    ? 'bg-white border-red-100 shadow-sm'
                    : 'bg-white border-gray-100 opacity-75'
                }`}
                data-testid={`notification-page-item-${notif.id}`}
              >
                <div className="flex gap-3 items-start">
                  <span className="text-xl flex-shrink-0 mt-0.5">{TYPE_ICONS[notif.type] || '🔔'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        {notif.title && (
                          <p className="font-semibold text-sm text-gray-900 leading-tight mb-1">{notif.title}</p>
                        )}
                        <p className="text-sm text-gray-600 leading-relaxed">{notif.message}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-xs text-gray-400">{getTimeAgo(notif.created_at)}</p>
                      {notif.type && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-gray-200 text-gray-500">
                          {typeLabels[notif.type] || notif.type}
                        </Badge>
                      )}
                      {notif.link && (
                        <span className="text-[10px] text-red-500 font-medium">Tap to open →</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Load more */}
            {notifications.length < total && !unreadOnly && (
              <div className="pt-4 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                  data-testid="load-more-btn"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  {labels.loadMore}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
